// src/components/PersonnelManagement.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PersonnelForm from './PersonnelForm';
import EmployeeAccessStep from './EmployeeAccessStep';
import PersonnelOnboardingWizard from './PersonnelOnboardingWizard';
import { ShieldAlert, UserPlus, Search, Edit2, Trash2, Key, List, FileText, CheckCircle2, XCircle, Info, AlertTriangle, ChevronsRight, Mail, Eye, User, ShieldCheck, Copy, Check, Filter, X } from 'lucide-react';
import { apiClient } from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import DataTable from './common/DataTable';
import InfoModal from './common/InfoModal';
import ColumnPicker from './common/ColumnPicker';
import useListColumnPreferences from '../hooks/useListColumnPreferences';

const FIXED_COLUMNS = ['fullName', 'sicilNo'];

const PERSONNEL_COLUMNS_POOL = [
    { header: 'ID', accessor: 'id', sortable: true, searchable: true },
    { header: 'Adı Soyadı', accessor: 'fullName', sortable: true, searchable: true },
    { header: 'Sicil No', accessor: 'sicilNo', sortable: true, searchable: true },
    { header: 'T.C. No', accessor: 'turkishIdNo', sortable: true, searchable: true },
    { header: 'Kullanıcı Adı', accessor: 'username', sortable: true, searchable: true },
    { header: 'Bölüm', accessor: 'departmentName', sortable: true, searchable: true },
    { header: 'Pozisyon', accessor: 'positionName', sortable: true, searchable: true },
    { header: 'Lokasyon', accessor: 'locationName', sortable: true, searchable: true },
    { header: 'Rol', accessor: 'userRole', sortable: true, searchable: true },
    { header: 'İşe Giriş Tarihi', accessor: 'hireDate', sortable: true, searchable: true },
    { header: 'İşten Çıkış Tarihi', accessor: 'terminationDate', sortable: true, searchable: true },
    { header: 'Telefon', accessor: 'phone', sortable: true, searchable: true },
    { header: 'Mail', accessor: 'email', sortable: true, searchable: true },
    { header: 'Cinsiyet', accessor: 'gender', sortable: true, searchable: true },
    { header: 'Kan Grubu', accessor: 'bloodType', sortable: true, searchable: true },
    { header: 'Çalışma Rejimi', accessor: 'workingRegimeName', sortable: true, searchable: true },
    { header: 'Şu Anki Vardiya', accessor: 'currentWorkPeriodName', sortable: true, searchable: true },
    { header: 'Durum', accessor: 'isActive', sortable: true, searchable: true },
];

const DEFAULT_VISIBLE_COLUMNS = ['id', 'fullName', 'sicilNo', 'turkishIdNo', 'departmentName', 'positionName', 'isActive'];


export default function PersonnelManagement() {
    const [personnelList, setPersonnelList] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPersonnel, setSelectedPersonnel] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const [rolesForDropdown, setRolesForDropdown] = useState([]);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [personnelToDeleteId, setPersonnelToDeleteId] = useState(null);
    const [wizardStep, setWizardStep] = useState(1);
    const [wasJustDeactivated, setWasJustDeactivated] = useState(false);

    // Hızlı filtre state'leri
    const [statusFilter, setStatusFilter] = useState('ALL'); // 'ALL' | 'ACTIVE' | 'INACTIVE'
    const [departmentFilter, setDepartmentFilter] = useState('ALL');
    const [locationFilter, setLocationFilter] = useState('ALL');

    // Bu state, alt component'i (EmployeeAccessStep) her seferinde sıfırlamak için kullanılacak.
    const [accessRefreshKey, setAccessRefreshKey] = useState(0);

    // Şifre sıfırlama modalları için state
    const [isPasswordResetConfirmOpen, setIsPasswordResetConfirmOpen] = useState(false);
    const [isPasswordResetResultOpen, setIsPasswordResetResultOpen] = useState(false);
    const [resetDeliveryMethod, setResetDeliveryMethod] = useState('SHOW'); // 'SHOW' or 'EMAIL'
    const [resetResultData, setResetResultData] = useState(null);
    const [selectedPersonnelForAction, setSelectedPersonnelForAction] = useState(null);
    const [copiedTempPassword, setCopiedTempPassword] = useState(false);

    const { isAuthenticated, permissions } = useAuth();
    const canResetPassword = useMemo(() => permissions?.includes('security:password:reset'), [permissions]);

    const canView = useMemo(() => permissions?.includes('personnel:view_list'), [permissions]);
    const canCreate = useMemo(() => permissions?.includes('personnel:create'), [permissions]);
    const canUpdate = useMemo(() => permissions?.includes('personnel:update'), [permissions]);
    const canDelete = useMemo(() => permissions?.includes('personnel:delete'), [permissions]);

    const { 
        visibleColumns, 
        updatePreferences, 
        resetPreferences, 
        isLoading: isPreferencesLoading 
    } = useListColumnPreferences(
        'personnel_management_list',
        PERSONNEL_COLUMNS_POOL,
        DEFAULT_VISIBLE_COLUMNS,
        FIXED_COLUMNS
    );

    const fetchInitialData = useCallback(async (preserveMessages = false) => {
        if (!canView) {
            setError('Personel listesini görüntüleme yetkiniz bulunmamaktadır.');
            setLoading(false);
            return;
        }
        setLoading(true);
        if (!preserveMessages) {
            setError(null);
            setMessage(null);
        }
        try {
            const [personnelData, rolesData] = await Promise.all([
                apiClient('Employee'),
                apiClient('roles/dropdown').catch(() => []),
            ]);
            
            const enrichedPersonnel = (personnelData || []).map(p => {
                const rawGender = p.details?.gender;
                const genderLabel =
                    rawGender === 'Male' || rawGender === 'M'
                        ? 'Erkek'
                        : rawGender === 'Female' || rawGender === 'F'
                            ? 'Kadın'
                            : '';

                return {
                    ...p,
                    fullName: `${p.firstName || ''} ${p.lastName || ''}`.trim(),
                    phone: p.phoneNumber || '',
                    gender: genderLabel,
                    bloodType: p.details?.bloodType || ''
                };
            });
            
            setPersonnelList(enrichedPersonnel);
            setRolesForDropdown(rolesData || []);
        } catch (err) {
            console.error('Veriler çekilirken hata:', err);
            setError(err.message || 'Verileri getirirken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    }, [canView]);

    useEffect(() => {
        if (isAuthenticated) {
            fetchInitialData();
        }
    }, [isAuthenticated, fetchInitialData]);

    const departmentOptions = useMemo(() => {
        const set = new Set();
        personnelList.forEach(p => {
            if (p.departmentName) set.add(p.departmentName);
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'tr'));
    }, [personnelList]);

    const locationOptions = useMemo(() => {
        const set = new Set();
        personnelList.forEach(p => {
            if (p.locationName) set.add(p.locationName);
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'tr'));
    }, [personnelList]);

    const statusCounts = useMemo(() => {
        let active = 0;
        let inactive = 0;
        personnelList.forEach(p => {
            if (p.isActive && !p.terminationDate) active++;
            else inactive++;
        });
        return { all: personnelList.length, active, inactive };
    }, [personnelList]);

    const filteredPersonnelList = useMemo(() => {
        return personnelList.filter(p => {
            const isPersonActive = p.isActive && !p.terminationDate;
            if (statusFilter === 'ACTIVE' && !isPersonActive) return false;
            if (statusFilter === 'INACTIVE' && isPersonActive) return false;
            if (departmentFilter !== 'ALL' && p.departmentName !== departmentFilter) return false;
            if (locationFilter !== 'ALL' && p.locationName !== locationFilter) return false;
            return true;
        });
    }, [personnelList, statusFilter, departmentFilter, locationFilter]);

    const handleAddClick = () => {
        setSelectedPersonnel(null);
        setWizardStep(1);
        setIsModalOpen(true);
        setMessage(null);
        setError(null);
        setWasJustDeactivated(false);
    };

    const handleEditClick = (person) => {
        setSelectedPersonnel(person);
        setWizardStep(1);
        setIsModalOpen(true);
        setMessage(null);
        setError(null);
        setWasJustDeactivated(false);
    };

    const handleDeleteRequest = (id) => {
        setPersonnelToDeleteId(id);
        setIsConfirmModalOpen(true);
    };

    const personnelToDelete = useMemo(
        () => personnelList.find(p => p.id === personnelToDeleteId) || null,
        [personnelList, personnelToDeleteId]
    );

    const handleDeleteConfirm = async () => {
        setIsConfirmModalOpen(false);
        if (personnelToDeleteId === null) return;
        setLoading(true);
        setError(null);
        setMessage(null);
        try {
            await apiClient(`Employee/${personnelToDeleteId}`, { method: 'DELETE' });
            await fetchInitialData(true);
            setMessage('Personel başarıyla silindi ve tüm cihaz/oturum yetkileri kaldırıldı!');
        } catch (err) {
            console.error('Personel silinirken hata:', err);
            setError(err.message || 'Personel silinirken bir hata oluştu.');
        } finally {
            setLoading(false);
            setPersonnelToDeleteId(null);
        }
    };

    const handleResetPasswordClick = (person) => {
        if (!person.userId) {
            setError('Bu personele tanımlı bir kullanıcı hesabı bulunmamaktadır. Lütfen personel düzenleme ekranından kaydederek hesabın otomatik oluşmasını sağlayın.');
            return;
        }
        setSelectedPersonnelForAction(person);
        setIsPasswordResetConfirmOpen(true);
        setResetDeliveryMethod('SHOW'); // Default to SHOW
        setCopiedTempPassword(false);
    };

    const handleResetPasswordConfirm = async () => {
        if (!selectedPersonnelForAction) return;
        const targetPerson = selectedPersonnelForAction;
        setIsPasswordResetConfirmOpen(false);

        setLoading(true);
        setError(null);
        setMessage(null);
        try {
            const result = await apiClient(`User/${targetPerson.userId}/reset-password`, {
                method: 'POST',
                body: { deliveryMethod: resetDeliveryMethod }
            });

            setResetResultData({
                ...result,
                username: result?.username || targetPerson.username || targetPerson.turkishIdNo,
                fullName: targetPerson.fullName || `${targetPerson.firstName || ''} ${targetPerson.lastName || ''}`.trim()
            });
            setCopiedTempPassword(false);

            if (resetDeliveryMethod === 'SHOW' && result.temporaryPassword) {
                setIsPasswordResetResultOpen(true);
                setPersonnelList(prev => prev.map(p => p.id === targetPerson.id ? { ...p, mustChangePassword: true } : p));
            } else if (result?.emailSent) {
                setMessage('Şifre sıfırlama bağlantısı personelin e-posta adresine başarıyla gönderildi.');
                setPersonnelList(prev => prev.map(p => p.id === targetPerson.id ? { ...p, mustChangePassword: true } : p));
            } else {
                setError('Şifre sıfırlama e-postası gönderilemedi.');
            }
        } catch (err) {
            console.error('Şifre sıfırlama hatası:', err);
            setError(err.message || 'Şifre sıfırlanırken bir hata oluştu.');
        } finally {
            setLoading(false);
            setSelectedPersonnelForAction(null);
        }
    };

    const handleCopyTemporaryPassword = () => {
        if (!resetResultData?.temporaryPassword) return;
        const copyText = resetResultData.username
            ? `Kullanıcı Adı: ${resetResultData.username}\nGeçici Şifre: ${resetResultData.temporaryPassword}`
            : resetResultData.temporaryPassword;
        navigator.clipboard?.writeText(copyText);
        setCopiedTempPassword(true);
        setTimeout(() => setCopiedTempPassword(false), 2500);
    };

    const handleFormSubmit = async (combinedData) => {
        const { personnelData, scopeData, detailsData, closeAfterSave } = combinedData;
        setLoading(true);
        setError(null);
        setMessage(null);

        try {
            const wasActive = selectedPersonnel ? selectedPersonnel.isActive : true;
            const method = personnelData.id ? 'PUT' : 'POST';
            const endpoint = personnelData.id ? `Employee/${personnelData.id}` : 'Employee';

            const savedPersonnel = await apiClient(endpoint, {
                method,
                body: personnelData,
            });

            const targetEmployeeId = personnelData.id || savedPersonnel?.id;
            if (!targetEmployeeId) throw new Error('Personel kaydedildi fakat ID alınamadı.');

            const targetUserId = savedPersonnel?.userId || selectedPersonnel?.userId;
            const partialWarnings = [];

            const parallelTasks = [];

            if (scopeData) {
                parallelTasks.push(
                    apiClient(`Employee/${targetEmployeeId}/datascope`, {
                        method: 'PUT',
                        body: scopeData,
                    }).catch(e => {
                        console.warn('datascope PUT atlandı:', e?.message || e);
                        partialWarnings.push(`Veri görme kapsamı güncellenemedi (${e?.message || 'Hata'})`);
                    })
                );
            }

            if (detailsData) {
                parallelTasks.push(
                    apiClient(`Employee/${targetEmployeeId}/details`, {
                        method: 'PUT',
                        body: detailsData,
                    }).catch(e => {
                        console.warn('IK Detaylari kaydedilemedi:', e?.message || e);
                        partialWarnings.push(`İK detayları güncellenemedi (${e?.message || 'Hata'})`);
                    })
                );
            }

            if (combinedData.photoFile) {
                const formDataPhoto = new FormData();
                formDataPhoto.append('file', combinedData.photoFile);
                parallelTasks.push(
                    apiClient(`EmployeeProfileMedia/${targetEmployeeId}/upload`, {
                        method: 'POST',
                        body: formDataPhoto,
                        headers: { 'Accept': 'application/json' },
                        isFormData: true
                    }).catch(photoErr => {
                        console.error("Fotoğraf yükleme hatası", photoErr);
                        partialWarnings.push(`Fotoğraf yüklenemedi (${photoErr?.message || 'Hata'})`);
                    })
                );
            }

            if (combinedData.subCompanyIds !== undefined && targetUserId) {
                parallelTasks.push(
                    apiClient('Holding/user-access', {
                        method: 'POST',
                        body: {
                            userId: targetUserId,
                            subCompanyIds: combinedData.subCompanyIds
                        }
                    }).catch(subErr => {
                        console.error("Alt firma yetkileri kaydedilemedi:", subErr);
                        partialWarnings.push(`Alt firma yetkileri güncellenemedi (${subErr?.message || 'Hata'})`);
                    })
                );
            }

            if (parallelTasks.length > 0) {
                await Promise.all(parallelTasks);
            }

            const becomesInactive = wasActive && !personnelData.isActive;
            setWasJustDeactivated(becomesInactive);

            await fetchInitialData(true);

            const warningSuffix = partialWarnings.length > 0 ? ` (Uyarı: ${partialWarnings.join(', ')})` : '';

            if (closeAfterSave) {
                handleModalClose();
                setMessage(`Personel bilgileri başarıyla güncellendi.${warningSuffix}`);
            } else {
                setMessage(`Personel kaydedildi. Kimlik & Cihaz adımına geçebilirsiniz.${warningSuffix}`);
                setSelectedPersonnel({
                    ...personnelData,
                    id: targetEmployeeId,
                    userId: targetUserId,
                    sicilNo: savedPersonnel?.sicilNo || personnelData.sicilNo,
                    username: savedPersonnel?.username || selectedPersonnel?.username,
                    details: detailsData
                });
                setWizardStep(2);
                setAccessRefreshKey(k => k + 1);
            }
        } catch (err) {
            console.error('Personel işlemi hatası:', err);
            setError(err.message || 'Personel işlemi sırasında beklenmedik bir hata oluştu.');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setIsConfirmModalOpen(false);
        setSelectedPersonnel(null);
        setPersonnelToDeleteId(null);
        setWizardStep(1);
        setError(null);
        setMessage(null);
        setWasJustDeactivated(false);
        setAccessRefreshKey(k => k + 1);
    };

    const getPersonnelRowClassName = useCallback((row, index) => {
        if (row.terminationDate) return 'bg-red-50/70 dark:bg-red-900/20 text-gray-500 dark:text-gray-400';
        if (!row.isActive) return 'bg-amber-50/70 dark:bg-amber-900/20';
        return index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700';
    }, []);

    const personnelColumns = useMemo(() => {
        const poolWithRenders = [
            { header: 'ID', accessor: 'id', sortable: true, searchable: true },
            {
                header: 'Adı Soyadı',
                accessor: 'fullName',
                sortable: true,
                searchable: true,
                render: row => (
                    <div className="flex flex-col">
                        <span className="font-semibold text-gray-900 dark:text-white">{row.fullName}</span>
                        {row.mustChangePassword && (
                            <span className="text-[10px] font-semibold text-amber-600 dark:text-amber-400">
                                İlk Giriş Şifresi Bekliyor
                            </span>
                        )}
                    </div>
                )
            },
            { header: 'Sicil No', accessor: 'sicilNo', sortable: true, searchable: true, render: row => <span className="font-mono font-semibold">{row.sicilNo || '-'}</span> },
            { header: 'T.C. No', accessor: 'turkishIdNo', sortable: true, searchable: true, render: row => <span className="font-mono">{row.turkishIdNo || '-'}</span> },
            { header: 'Kullanıcı Adı', accessor: 'username', sortable: true, searchable: true, render: row => <span className="font-mono text-indigo-600 dark:text-indigo-400 font-semibold">{row.username || row.turkishIdNo || '-'}</span> },
            { header: 'Bölüm', accessor: 'departmentName', sortable: true, searchable: true, render: row => row.departmentName || '-' },
            { header: 'Pozisyon', accessor: 'positionName', sortable: true, searchable: true, render: row => row.positionName || '-' },
            { header: 'Lokasyon', accessor: 'locationName', sortable: true, searchable: true, render: row => row.locationName || '-' },
            { header: 'Rol', accessor: 'userRole', sortable: true, searchable: true, render: row => row.userRole || '-' },
            {
                header: 'İşe Giriş Tarihi',
                accessor: 'hireDate',
                sortable: true,
                searchable: true,
                getFilterValue: row => row.hireDate ? new Date(row.hireDate).toLocaleDateString('tr-TR') : '',
                render: row => row.hireDate ? new Date(row.hireDate).toLocaleDateString('tr-TR') : '-'
            },
            {
                header: 'İşten Çıkış Tarihi',
                accessor: 'terminationDate',
                sortable: true,
                searchable: true,
                getFilterValue: row => row.terminationDate ? new Date(row.terminationDate).toLocaleDateString('tr-TR') : '',
                render: row => row.terminationDate ? new Date(row.terminationDate).toLocaleDateString('tr-TR') : '-'
            },
            {
                header: 'Telefon',
                accessor: 'phone',
                sortable: true,
                searchable: true,
                getFilterValue: row => row.phoneNumber ? `${row.phoneNumber} 0 (${row.phoneNumber.slice(0, 3)}) ${row.phoneNumber.slice(3, 6)} ${row.phoneNumber.slice(6, 8)} ${row.phoneNumber.slice(8, 10)}` : '',
                render: row => row.phoneNumber ? `0 (${row.phoneNumber.slice(0, 3)}) ${row.phoneNumber.slice(3, 6)} ${row.phoneNumber.slice(6, 8)} ${row.phoneNumber.slice(8, 10)}` : '-'
            },
            { header: 'Mail', accessor: 'email', sortable: true, searchable: true, render: row => row.email || '-' },
            { header: 'Cinsiyet', accessor: 'gender', sortable: true, searchable: true, render: row => row.gender || '-' },
            { header: 'Kan Grubu', accessor: 'bloodType', sortable: true, searchable: true, render: row => row.bloodType || '-' },
            {
                header: 'Çalışma Rejimi',
                accessor: 'workingRegimeName',
                sortable: true,
                searchable: true,
                getFilterValue: row => `${row.workingRegimeName || ''} ${row.currentWorkPeriodName || ''}`,
                render: row => (
                    <div className="flex flex-col gap-0.5">
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                            {row.workingRegimeName || '-'}
                        </span>
                        {row.currentWorkPeriodName && (row.isCurrentScheduleDifferentFromRegime || row.isCurrentScheduleTemporary) && (
                            <span
                                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 w-fit"
                                title={row.isCurrentScheduleTemporary ? 'Geçici vardiya ataması aktif' : `Rejim varsayılanından (${row.regimeDefaultWorkPeriodName || '-'}) farklı vardiya`}
                            >
                                Şu An: {row.currentWorkPeriodName}
                            </span>
                        )}
                    </div>
                )
            },
            {
                header: 'Şu Anki Vardiya',
                accessor: 'currentWorkPeriodName',
                sortable: true,
                searchable: true,
                getFilterValue: row => row.currentWorkPeriodName || 'Atanmamış',
                render: row => {
                    if (!row.currentWorkPeriodName) {
                        return (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                                Atanmamış
                            </span>
                        );
                    }
                    const isSpecial = row.isCurrentScheduleDifferentFromRegime || row.isCurrentScheduleTemporary;
                    return (
                        <div className="flex flex-col gap-0.5">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold w-fit ${
                                isSpecial
                                    ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300'
                                    : 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                            }`}>
                                {row.currentWorkPeriodName}
                            </span>
                            {row.isCurrentScheduleTemporary && row.currentScheduleEndDate && (
                                <span className="text-[10px] text-amber-600 dark:text-amber-400 font-medium">
                                    Geçici ({new Date(row.currentScheduleEndDate).toLocaleDateString('tr-TR')})
                                </span>
                            )}
                            {!row.isCurrentScheduleTemporary && row.isCurrentScheduleDifferentFromRegime && (
                                <span className="text-[10px] text-amber-600 dark:text-amber-400 font-medium">
                                    Rejimden Farklı ({row.workingRegimeName || '-'})
                                </span>
                            )}
                        </div>
                    );
                }
            },
            {
                header: 'Durum',
                accessor: 'isActive',
                sortable: true,
                searchable: true,
                getFilterValue: row => row.terminationDate ? 'İşten Ayrıldı Pasif' : (row.isActive ? 'Aktif' : 'Pasif'),
                render: row => {
                    if (row.terminationDate) {
                        return (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
                                İşten Ayrıldı
                            </span>
                        );
                    }
                    return row.isActive ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                            Aktif
                        </span>
                    ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                            Pasif
                        </span>
                    );
                }
            },
        ];

        const columns = visibleColumns.map(accessor => poolWithRenders.find(c => c.accessor === accessor)).filter(Boolean);

        if (canUpdate || canDelete || canResetPassword) {
            columns.push({
                header: 'Aksiyonlar',
                accessor: 'actions',
                render: (row) => (
                    <div className="flex space-x-2">
                        {canUpdate && (
                            <button onClick={() => handleEditClick(row)} className="p-1.5 rounded-full text-indigo-400 hover:bg-gray-700" title="Düzenle">
                                <Edit2 className="w-5 h-5" strokeWidth={2.2} />
                            </button>
                        )}
                        {canDelete && (
                            <button onClick={() => handleDeleteRequest(row.id)} className="p-1.5 rounded-full text-red-400 hover:bg-gray-700" title="Sil">
                                <Trash2 className="w-5 h-5" strokeWidth={2.2} />
                            </button>
                        )}
                        {canResetPassword && (
                            <button onClick={() => handleResetPasswordClick(row)} className="p-1.5 rounded-full text-amber-400 hover:bg-gray-700" title="Şifre Sıfırla">
                                <ShieldAlert className="w-5 h-5" strokeWidth={2.2} />
                            </button>
                        )}
                    </div>
                ),
            });
        }
        return columns;
    }, [canUpdate, canDelete, canResetPassword, visibleColumns]);

    const modalTitle = wizardStep === 1
        ? (selectedPersonnel?.id ? 'Personel Düzenle – Adım 1/2' : 'Yeni Personel – Adım 1/2')
        : `Kimlik & Cihaz – Adım 2/2 ${selectedPersonnel ? `(${selectedPersonnel.firstName || ''} ${selectedPersonnel.lastName || ''})` : ''}`;

    const hasValidEmailForReset = Boolean(
        selectedPersonnelForAction?.email &&
        !String(selectedPersonnelForAction.email).startsWith('no-email-')
    );

    return (
        <div className="p-1 bg-gray-100 dark:bg-gray-900 min-h-full">
            {message && (
                <div className="mb-4 flex items-center p-3 text-sm text-green-800 rounded-lg bg-green-50 dark:bg-gray-800 dark:text-green-400" role="alert">
                    <CheckCircle2 className="flex-shrink-0 inline w-4 h-4 me-3" />
                    <div>{message}</div>
                </div>
            )}
            {error && (
                <div className="mb-4 flex items-center p-3 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400" role="alert">
                    <XCircle className="flex-shrink-0 inline w-4 h-4 me-3" />
                    <div>{error}</div>
                </div>
            )}

            {/* Hızlı Filtre Barı (Durum, Bölüm, Lokasyon) */}
            <div className="mb-3 p-3 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 mr-1 inline-flex items-center gap-1">
                        <Filter className="w-3.5 h-3.5" /> Durum:
                    </span>
                    <button
                        type="button"
                        onClick={() => setStatusFilter('ALL')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                            statusFilter === 'ALL'
                                ? 'bg-indigo-600 text-white shadow-sm'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                    >
                        Tümü ({statusCounts.all})
                    </button>
                    <button
                        type="button"
                        onClick={() => setStatusFilter('ACTIVE')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                            statusFilter === 'ACTIVE'
                                ? 'bg-emerald-600 text-white shadow-sm'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                    >
                        Aktif ({statusCounts.active})
                    </button>
                    <button
                        type="button"
                        onClick={() => setStatusFilter('INACTIVE')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                            statusFilter === 'INACTIVE'
                                ? 'bg-rose-600 text-white shadow-sm'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                    >
                        Pasif / Ayrılan ({statusCounts.inactive})
                    </button>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                    <select
                        value={departmentFilter}
                        onChange={(e) => setDepartmentFilter(e.target.value)}
                        className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-indigo-500"
                    >
                        <option value="ALL">Tüm Bölümler</option>
                        {departmentOptions.map(dep => (
                            <option key={dep} value={dep}>{dep}</option>
                        ))}
                    </select>

                    <select
                        value={locationFilter}
                        onChange={(e) => setLocationFilter(e.target.value)}
                        className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-indigo-500"
                    >
                        <option value="ALL">Tüm Lokasyonlar</option>
                        {locationOptions.map(loc => (
                            <option key={loc} value={loc}>{loc}</option>
                        ))}
                    </select>

                    {(statusFilter !== 'ALL' || departmentFilter !== 'ALL' || locationFilter !== 'ALL') && (
                        <button
                            type="button"
                            onClick={() => {
                                setStatusFilter('ALL');
                                setDepartmentFilter('ALL');
                                setLocationFilter('ALL');
                            }}
                            className="px-2.5 py-1.5 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
                        >
                            Filtreleri Temizle
                        </button>
                    )}
                </div>
            </div>

            <DataTable
                data={filteredPersonnelList}
                columns={personnelColumns}
                itemsPerPage={10}
                showSearch={true}
                searchPlaceholder="Ad, Soyad, Sicil No, T.C., Bölüm veya Lokasyon ara..."
                showPagination={true}
                emptyMessage="Filtrelere uygun personel bulunamadı."
                isLoading={loading || isPreferencesLoading}
                getRowClassName={getPersonnelRowClassName}
                actionsHeader={
                    <div className="flex items-center space-x-4">
                        <p className="text-gray-600 dark:text-gray-300 text-sm hidden md:block">
                            Listelenen: <strong>{filteredPersonnelList.length}</strong> / {personnelList.length}
                        </p>
                        <ColumnPicker 
                            columnsPool={PERSONNEL_COLUMNS_POOL} 
                            visibleColumns={visibleColumns}
                            fixedColumns={FIXED_COLUMNS}
                            onSave={updatePreferences}
                            onReset={resetPreferences}
                            maxColumns={7}
                        />
                        {canCreate && (
                            <button
                                onClick={handleAddClick}
                                className="flex items-center px-4 py-2 bg-green-600 text-white font-semibold rounded-md shadow-sm hover:bg-green-700">
                                <UserPlus className="w-5 h-5 mr-2" />
                                Personel Ekle
                            </button>
                        )}
                    </div>
                }
            />

            <div className="md:hidden text-center text-gray-600 dark:text-gray-300 text-sm mt-4">
                Listelenen Personel: {filteredPersonnelList.length} / {personnelList.length}
            </div>

            {isModalOpen && !selectedPersonnel ? (
                <PersonnelOnboardingWizard 
                    onClose={handleModalClose} 
                    onSuccess={() => {
                        handleModalClose();
                        fetchInitialData();
                    }} 
                />
            ) : isModalOpen ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-5 bg-gray-900/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div
                        className="fixed inset-0"
                        onClick={!loading ? handleModalClose : undefined}
                    />
                    <div className="relative bg-white dark:bg-gray-900 w-full max-w-7xl rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 flex flex-col max-h-[94vh] overflow-hidden z-10">
                        {/* Kompakt Başlık + Yatay Adım Sekmeleri */}
                        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800 flex flex-wrap items-center justify-between gap-3 bg-gray-50/70 dark:bg-gray-800/40 shrink-0">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-9 h-9 rounded-xl bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0">
                                    <User size={20} />
                                </div>
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className="text-base md:text-lg font-bold text-gray-900 dark:text-white truncate">
                                            {modalTitle}
                                        </h3>
                                        {selectedPersonnel?.sicilNo && (
                                            <span className="px-2 py-0.5 text-xs font-mono font-semibold rounded-md bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-200/60 dark:border-indigo-800/60">
                                                Sicil: {selectedPersonnel.sicilNo}
                                            </span>
                                        )}
                                        {selectedPersonnel?.username && (
                                            <span className="px-2 py-0.5 text-xs font-mono font-medium rounded-md bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
                                                Kullanıcı Adı: {selectedPersonnel.username}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Yatay Sekmeli Adım Geçişi (Yüksekliği Kısaltan Kompakt Tasarım) */}
                            <div className="flex items-center gap-2">
                                <div className="inline-flex p-1 rounded-xl bg-gray-200/70 dark:bg-gray-800 border border-gray-300/60 dark:border-gray-700">
                                    <button
                                        type="button"
                                        onClick={() => setWizardStep(1)}
                                        className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                            wizardStep === 1
                                                ? 'bg-white dark:bg-indigo-600 text-indigo-700 dark:text-white shadow-sm'
                                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                                        }`}
                                    >
                                        {wizardStep > 1 ? <CheckCircle2 size={14} className="text-emerald-500" /> : <User size={14} />}
                                        <span>1. Personel &amp; Yetki Bilgileri</span>
                                    </button>
                                    <button
                                        type="button"
                                        disabled={!selectedPersonnel?.id}
                                        onClick={() => {
                                            if (selectedPersonnel?.id) {
                                                setWizardStep(2);
                                            }
                                        }}
                                        title={selectedPersonnel?.id ? 'Kimlik & Cihaz adımına doğrudan geç' : 'Önce personel bilgilerini kaydedin'}
                                        className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                            !selectedPersonnel?.id
                                                ? 'opacity-50 cursor-not-allowed text-gray-400'
                                                : wizardStep === 2
                                                    ? 'bg-white dark:bg-indigo-600 text-indigo-700 dark:text-white shadow-sm'
                                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                                        }`}
                                    >
                                        <ShieldCheck size={14} />
                                        <span>2. Kimlik Kartı &amp; Cihaz Yetkisi</span>
                                    </button>
                                </div>

                                {!loading && (
                                    <button
                                        type="button"
                                        onClick={handleModalClose}
                                        className="p-1.5 rounded-full hover:bg-gray-200/70 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                                        title="Kapat"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* İçerik Gövdesi */}
                        <div className="flex-1 overflow-y-auto px-5 py-4 custom-scrollbar">
                            {wizardStep === 1 ? (
                                <PersonnelForm
                                    personnel={selectedPersonnel}
                                    onSave={handleFormSubmit}
                                    onCancel={handleModalClose}
                                    isNew={!selectedPersonnel?.id}
                                    submitting={loading}
                                    roles={rolesForDropdown}
                                />
                            ) : (
                                <div className="space-y-4">
                                    <EmployeeAccessStep
                                        key={accessRefreshKey}
                                        employee={selectedPersonnel}
                                        wasJustDeactivated={wasJustDeactivated}
                                    />

                                    <div className="flex justify-between items-center pt-3 border-t border-gray-200 dark:border-gray-800">
                                        <button
                                            type="button"
                                            onClick={() => setWizardStep(1)}
                                            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-800 dark:text-white transition-colors"
                                        >
                                            <Key className="w-4 h-4 mr-1.5" /> Personel Bilgilerine Dön (Adım 1)
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleModalClose}
                                            className="inline-flex items-center px-5 py-2 text-sm font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm transition-colors"
                                        >
                                            Tamamla &amp; Kapat
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ) : null}

            <InfoModal
                isOpen={isConfirmModalOpen}
                onClose={() => {
                    setIsConfirmModalOpen(false);
                    setPersonnelToDeleteId(null);
                }}
                title="Silme Onayı"
                message={
                    personnelToDelete
                        ? `${personnelToDelete.fullName} (Sicil: ${personnelToDelete.sicilNo || '-'}) adlı personeli silmek istediğinizden emin misiniz? Silinen personelin tüm kart/cihaz geçiş yetkileri ve aktif oturumları derhal iptal edilir.`
                        : "Bu personeli silmek istediğinizden emin misiniz? Silinen personelin tüm kart/cihaz geçiş yetkileri ve oturumları derhal iptal edilir."
                }
                type="confirm"
                onConfirm={handleDeleteConfirm}
            />

            {/* Password Reset Confirmation Modal */}
            <InfoModal
                isOpen={isPasswordResetConfirmOpen}
                onClose={() => setIsPasswordResetConfirmOpen(false)}
                title="Şifre Sıfırlama Onayı"
                type="warning"
                confirmText="Evet, Sıfırla"
                onConfirm={handleResetPasswordConfirm}
            >
                <div className="space-y-4">
                    <p className="text-gray-600 dark:text-gray-400">
                        <span className="font-semibold text-gray-900 dark:text-white">{selectedPersonnelForAction?.firstName} {selectedPersonnelForAction?.lastName}</span> adlı personelin şifresi sıfırlanacaktır. Bu işlem geri alınamaz.
                    </p>

                    <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-700 space-y-2.5">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Gönderme Yöntemi Seçin:</p>
                        <div
                            onClick={() => setResetDeliveryMethod('SHOW')}
                            className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all ${resetDeliveryMethod === 'SHOW' ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/20 ring-1 ring-emerald-500 outline-none' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                        >
                            <input
                                type="radio"
                                name="deliveryMethod"
                                className="hidden"
                                value="SHOW"
                                checked={resetDeliveryMethod === 'SHOW'}
                                readOnly
                            />
                            <div className={`p-2 rounded-lg mr-3 transition-colors ${resetDeliveryMethod === 'SHOW' ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'}`}>
                                <Eye size={20} />
                            </div>
                            <div className="flex-1">
                                <div className={`text-sm font-semibold transition-colors ${resetDeliveryMethod === 'SHOW' ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-900 dark:text-white'}`}>Ekranda Göster</div>
                                <div className="text-xs text-gray-500">Geçici şifreyi şimdi ekranda görüntüler (İlk girişte şifre değişimi zorunlu olur).</div>
                            </div>
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${resetDeliveryMethod === 'SHOW' ? 'border-emerald-500 bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]' : 'border-gray-300 dark:border-gray-600'}`}>
                                {resetDeliveryMethod === 'SHOW' && <div className="w-2 h-2 rounded-full bg-white transition-all scale-110" />}
                            </div>
                        </div>

                        <div
                            onClick={() => {
                                if (hasValidEmailForReset) {
                                    setResetDeliveryMethod('EMAIL');
                                }
                            }}
                            className={`flex items-center p-3 rounded-lg border transition-all ${
                                !hasValidEmailForReset
                                    ? 'border-gray-200 dark:border-gray-800 opacity-50 cursor-not-allowed bg-gray-100/50 dark:bg-gray-800/30'
                                    : resetDeliveryMethod === 'EMAIL'
                                        ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/20 ring-1 ring-emerald-500 outline-none cursor-pointer'
                                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer'
                            }`}
                        >
                            <input
                                type="radio"
                                name="deliveryMethod"
                                className="hidden"
                                value="EMAIL"
                                disabled={!hasValidEmailForReset}
                                checked={resetDeliveryMethod === 'EMAIL'}
                                readOnly
                            />
                            <div className={`p-2 rounded-lg mr-3 transition-colors ${resetDeliveryMethod === 'EMAIL' ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'}`}>
                                <Mail size={20} />
                            </div>
                            <div className="flex-1">
                                <div className={`text-sm font-semibold transition-colors ${resetDeliveryMethod === 'EMAIL' ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-900 dark:text-white'}`}>E-posta Gönder</div>
                                <div className="text-xs text-gray-500">
                                    {hasValidEmailForReset
                                        ? `Şifre sıfırlama bağlantısını (${selectedPersonnelForAction.email}) adresine gönderir.`
                                        : 'Bu personelin tanımlı bir e-posta adresi bulunmamaktadır.'}
                                </div>
                            </div>
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${resetDeliveryMethod === 'EMAIL' ? 'border-emerald-500 bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]' : 'border-gray-300 dark:border-gray-600'}`}>
                                {resetDeliveryMethod === 'EMAIL' && <div className="w-2 h-2 rounded-full bg-white transition-all scale-110" />}
                            </div>
                        </div>
                    </div>
                </div>
            </InfoModal>

            <InfoModal
                isOpen={isPasswordResetResultOpen}
                onClose={() => setIsPasswordResetResultOpen(false)}
                title="Geçici Şifre Oluşturuldu"
                type="info"
            >
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl mt-2 text-center space-y-3">
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                        {resetResultData?.fullName ? <strong>{resetResultData.fullName}</strong> : 'Personel'} için geçici giriş bilgileri oluşturuldu. Güvenlik gereği personel ilk giriş yaptığında şifresini değiştirmek zorundadır:
                    </p>
                    {resetResultData?.username && (
                        <div className="flex items-center justify-between py-2.5 px-4 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 text-sm">
                            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Kullanıcı Adı</span>
                            <span className="font-mono font-bold text-gray-900 dark:text-white">{resetResultData.username}</span>
                        </div>
                    )}
                    <div className="flex items-center justify-between gap-2 py-3 px-4 bg-white dark:bg-gray-900 rounded-lg border border-indigo-100 dark:border-indigo-900 shadow-inner">
                        <div className="text-left">
                            <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider block">Geçici Şifre</span>
                            <span className="text-2xl font-mono font-bold tracking-widest text-indigo-600 dark:text-indigo-400">
                                {resetResultData?.temporaryPassword}
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={handleCopyTemporaryPassword}
                            className="ml-2 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/60 transition-colors"
                            title="Kullanıcı Adı ve Şifreyi Panoya Kopyala"
                        >
                            {copiedTempPassword ? <Check size={14} /> : <Copy size={14} />}
                            {copiedTempPassword ? 'Kopyalandı' : 'Bilgileri Kopyala'}
                        </button>
                    </div>
                </div>
                <div className="mt-4 flex justify-center">
                    <button
                        onClick={() => setIsPasswordResetResultOpen(false)}
                        className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold transition-colors"
                    >
                        Anladım, Kapat
                    </button>
                </div>
            </InfoModal>
        </div>
    );
}